import type { PublicInvoicePay } from '../model/types';

/**
 * Dev-only scenarios for `?testing009` mock panel.
 *
 * Mock invoice id is passed through as `PublicInvoicePay.id`.
 * Scenarios map to UI states:
 * - `details` → DETAILS (pending, no address, confirmations 0/0)
 * - `payment` → PAYMENT (address shown, confirmations 0/19)
 * - `payment_detected` → PAYMENT_DETECTED (txid, 0/19)
 * - `confirming` → CONFIRMING (7/19)
 * - `success` → SUCCESS paid (19/19)
 */
export type InvoicePayMockScenario =
  | 'loading'
  | 'details'
  | 'payment'
  | 'payment_underpaid'
  | 'payment_detected'
  | 'confirming'
  | 'success'
  | 'success_overpaid'
  | 'expired'
  | 'error'
  | 'load_error';

export const INVOICE_PAY_MOCK_SCENARIOS: {
  id: InvoicePayMockScenario;
  label: string;
  hint: string;
}[] = [
  { id: 'loading', label: 'LOADING', hint: 'Skeleton first paint' },
  { id: 'details', label: 'DETAILS', hint: 'pending, no address yet' },
  { id: 'payment', label: 'PAYMENT', hint: 'QR + address, waiting' },
  { id: 'payment_underpaid', label: 'PAYMENT (underpaid)', hint: 'status underpaid + remaining' },
  { id: 'payment_detected', label: 'PAYMENT_DETECTED', hint: 'txid seen, 0 confirmations' },
  { id: 'confirming', label: 'CONFIRMING', hint: 'confirmations in progress' },
  { id: 'success', label: 'SUCCESS (paid)', hint: 'status paid + txid' },
  { id: 'success_overpaid', label: 'SUCCESS (overpaid)', hint: 'status overpaid' },
  { id: 'expired', label: 'EXPIRED', hint: 'status expired' },
  { id: 'error', label: 'ERROR (invoice)', hint: 'status error from backend' },
  { id: 'load_error', label: 'ERROR (load)', hint: 'GET failed / 404' },
];

const MOCK_ADDRESS = 'TH8TPzj51cYwEnSyC9RKzqWiuk7jGhyPvw';
const MOCK_TXID = '0xabc123def4567890abcdef1234567890abcdef1234567890abcdef1234567890';
const MOCK_CONFIRMATIONS_REQUIRED = 19;

const baseCurrencies: PublicInvoicePay['availableCurrencies'] = [
  {
    code: 'USDT',
    networks: [
      { code: 'TRC20', label: 'TRON (TRX 20)' },
      { code: 'ERC20', label: 'Ethereum (ERC 20)' },
      { code: 'BEP20', label: 'BNB Smart Chain (BEP 20)' },
    ],
  },
  {
    code: 'BTC',
    networks: [{ code: 'BTC', label: 'Bitcoin' }],
  },
];

const createBase = (invoiceId: string): PublicInvoicePay => ({
  id: invoiceId || 'mock-invoice-id',
  status: 'pending',
  amount: '100.0000',
  currency: 'USDT',
  network: 'TRC20',
  networkLabel: 'TRON (TRX 20)',
  amountUsd: '100.0000',
  address: null,
  qrUrl: null,
  expiresAt: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
  paidAmount: '0',
  remainingAmount: null,
  confirmations: { current: 0, required: 0 },
  txid: null,
  availableCurrencies: baseCurrencies,
  returnUrl: 'https://my.paygatex.io/',
  supportUrl: 'mailto:support@paygatex.io',
  serverTime: new Date().toISOString(),
});

export const buildMockInvoice = (
  invoiceId: string,
  scenario: InvoicePayMockScenario
): PublicInvoicePay | null => {
  if (scenario === 'loading' || scenario === 'load_error') return null;

  const base = createBase(invoiceId);

  switch (scenario) {
    case 'details':
      return { ...base, status: 'pending', address: null, qrUrl: null };
    case 'payment':
      return {
        ...base,
        status: 'pending',
        address: MOCK_ADDRESS,
        qrUrl: null,
        confirmations: { current: 0, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'payment_underpaid':
      return {
        ...base,
        status: 'underpaid',
        address: MOCK_ADDRESS,
        paidAmount: '40.0000',
        remainingAmount: '60.0000',
        confirmations: { current: 0, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'payment_detected':
      return {
        ...base,
        status: 'pending',
        address: MOCK_ADDRESS,
        txid: MOCK_TXID,
        confirmations: { current: 0, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'confirming':
      return {
        ...base,
        status: 'pending',
        address: MOCK_ADDRESS,
        txid: MOCK_TXID,
        confirmations: { current: 7, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'success':
      return {
        ...base,
        status: 'paid',
        address: MOCK_ADDRESS,
        txid: MOCK_TXID,
        paidAmount: '100.0000',
        confirmations: { current: MOCK_CONFIRMATIONS_REQUIRED, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'success_overpaid':
      return {
        ...base,
        status: 'overpaid',
        address: MOCK_ADDRESS,
        txid: MOCK_TXID,
        amount: '100.0000',
        paidAmount: '105.0000',
        confirmations: { current: MOCK_CONFIRMATIONS_REQUIRED, required: MOCK_CONFIRMATIONS_REQUIRED },
      };
    case 'expired':
      return {
        ...base,
        status: 'expired',
        address: MOCK_ADDRESS,
        expiresAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      };
    case 'error':
      return {
        ...base,
        status: 'error',
        address: MOCK_ADDRESS,
        txid: null,
      };
    default:
      return base;
  }
};

/** Query flag for invoice pay mock tooling. Presence enables mocks (any value). */
export const isInvoicePayTestingQuery = (search: string): boolean => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.has('testing009');
};
